/**
 * Order Attributes Builder
 * Standalone module for building and saving customization data to order attributes
 *
 * This module can be used by:
 * - customizer-v2.js (full customization flow)
 * - Logo-only product pages
 * - Any future integration needing order attribute storage
 *
 * Usage:
 *   // Build and save in one call
 *   await OrderAttributesBuilder.saveCustomizations({
 *     variantId: '12345',
 *     selectedLocations: ['left-chest', 'right-sleeve'],
 *     locationState: { ... },
 *     metadata: { storefront: '5835', shopDomain: '...' }
 *   });
 *
 *   // Or build and save separately
 *   const json = OrderAttributesBuilder.buildCustomizationJSON(config);
 *   await OrderAttributesBuilder.updateCartCustomizations(json);
 */
(function() {
  'use strict';

  window.OrderAttributesBuilder = {
    /**
     * Check if current session is B2B
     * @returns {boolean}
     */
    isB2BSession() {
      return window.__st?.b2b === true ||
             document.body.classList.contains('b2b-customer') ||
             window.Shopify?.customer?.b2b === true ||
             !!window.CustomizerV2Data?.isB2B;
    },

    /**
     * Get next available index for a variant in cart
     * Used to generate unique keys like variant_12345_0, variant_12345_1, etc.
     * @param {string} variantId - The variant ID
     * @returns {Promise<number>}
     */
    async getNextItemIndex(variantId) {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        let maxIndex = -1;
        const existingCustomizations = cart.attributes?._customizations;

        if (existingCustomizations) {
          const parsed = typeof existingCustomizations === 'string' ?
            JSON.parse(existingCustomizations) : existingCustomizations;

          Object.keys(parsed).forEach(key => {
            if (key.startsWith(`variant_${variantId}_`)) {
              const idx = parseInt(key.split('_').pop(), 10);
              if (idx > maxIndex) maxIndex = idx;
            }
          });
        }

        return maxIndex + 1;
      } catch (e) {
        return 0;
      }
    },

    /**
     * Build customization JSON for order attributes
     * @param {Object} config - Configuration object
     * @param {string} config.variantId - The variant ID
     * @param {number} [config.itemIndex=0] - Index for this variant (0, 1, 2...)
     * @param {Array<string>} config.selectedLocations - Array of selected location IDs
     * @param {Array<Object>} config.locations - Array of location objects with id, name, picture
     * @param {Object} config.locationState - State per location { [locationId]: { selectedLogo, logoPosition, ... } }
     * @param {Object} config.metadata - Metadata object { storefront, shopDomain, productType, etc. }
     * @param {number} [config.canvasWidth=600] - Canvas width for coordinate scaling
     * @param {number} [config.canvasHeight=600] - Canvas height for coordinate scaling
     * @param {Object} [config.locationUrls] - Pre-computed deterministic screenshot URLs
     *   keyed by locationId (e.g. { front: { mockup, logo, text, filenames }, ... }) or
     *   by 'product' for the no-locations path. When provided, urls are written into
     *   each locationData (or the entry itself) in the SAME atomic write as the rest
     *   of the customization — this eliminates the post-capture second-write race
     *   that could clobber urls when multiple items were added in quick succession.
     * @returns {Object} Object with key and customization data: { "variant_123_0": { ... } }
     */
    buildCustomizationJSON(config) {
      const {
        variantId,
        itemIndex = 0,
        selectedLocations = [],
        locations = [],
        locationState = {},
        metadata = {},
        canvasWidth = 600,
        canvasHeight = 600,
        locationUrls = null
      } = config;

      const key = `variant_${variantId}_${itemIndex}`;

      const customization = {
        locations: [],
        metadata: {
          storefront: metadata.storefront || '',
          shopDomain: metadata.shopDomain || '',
          productType: metadata.productType || '',
          fulfiller: metadata.fulfiller || '',
          supplier: metadata.supplier || '',
          decorationType: metadata.decorationType || '',
          brand: metadata.brand || '',
          variantTitle: metadata.variantTitle || '',
          color: metadata.color || '',
          size: metadata.size || '',
          timestamp: new Date().toISOString()
        }
      };

      // No-locations path — attach entry-level urls (e.g. product-only mockup)
      if (locationUrls && locationUrls.product && selectedLocations.length === 0) {
        customization.urls = {
          mockup: locationUrls.product.mockup || null,
          logo: null,
          text: null
        };
      }

      selectedLocations.forEach((locationId, i) => {
        const index = i + 1;
        const location = locations.find(l => l.id === locationId);
        const locState = locationState[locationId];

        if (!location) return;

        const locationData = {
          index: index,
          name: location.name,
          id: locationId,
          productImage: location.picture || metadata.fallbackImage || ''
        };

        if (locState?.noLogo) {
          locationData.noLogo = true;
        }

        if (locState?.selectedLogo && !locState?.noLogo) {
          const logoPosition = locState.logoPosition;
          const customColors = locState.logoThreadColors?.[locState.selectedLogo.id] || {};
          const svgThreadLayers = locState.svgThreadLayers?.[locState.selectedLogo.id] || [];

          const threads = [];
          svgThreadLayers.forEach(layer => {
            const custom = customColors[layer.index];
            threads.push({
              name: custom?.name || layer.name,
              hex: custom?.hex || layer.defaultHex,
              code: custom?.code || layer.code || ''
            });
          });

          const cw = logoPosition?.canvasWidth || canvasWidth;
          const ch = logoPosition?.canvasHeight || canvasHeight;

          locationData.logo = {
            id: locState.selectedLogo.id,
            url: locState.selectedLogo.url || null,
            code: `${metadata.storefront}.${locState.selectedLogo.id}`,
            coordinates: logoPosition ?
              `${Math.round(logoPosition.left)}_${Math.round(logoPosition.top)}_${Math.round(logoPosition.scaleX * 100)}_${Math.round(logoPosition.scaleY * 100)}_${cw}_${ch}` : null,
            rotateAngle: logoPosition?.angle || 0,
            threads: threads
          };
        }

        if (locState?.personalization?.text?.trim()) {
          const p = locState.personalization;
          const position = p.position;
          const cw = position?.canvasWidth || canvasWidth;
          const ch = position?.canvasHeight || canvasHeight;

          const defaultFont = window.CustomizerV2Data?.personalizationFonts?.[0] || { name: 'Arial', family: 'Arial, sans-serif' };
          let fontName = defaultFont.name;
          let fontFamily = defaultFont.family;
          if (p.font) {
            if (typeof p.font === 'object') {
              fontName = p.font.name || defaultFont.name;
              fontFamily = p.font.family || defaultFont.family;
            } else {
              fontName = p.font;
              fontFamily = p.font;
            }
          }

          locationData.personalization = {
            text: p.text.trim(),
            font: fontName,
            fontFamily: fontFamily,
            fontSize: position?.fontSize || 40,
            textAlign: p.textAlign || 'center',
            coordinates: position ?
              `${Math.round(position.left)}_${Math.round(position.top)}_${Math.round(position.fontSize)}_${Math.round(position.angle || 0)}_${cw}_${ch}` : null,
            threadColor: p.threadColor?.hex || '#FFFFFF',
            threadName: p.threadColor?.name || 'White',
            threadCode: p.threadColor?.code || '001'
          };
        }

        // Attach deterministic screenshot URLs alongside the logo/personalization
        // data. These point at filenames the capture step will upload to; writing
        // them here (rather than in a second POST after upload) means a later
        // saveCustomizations for another item can never clobber them.
        if (locationUrls && locationUrls[locationId]) {
          const urls = locationUrls[locationId];
          locationData.urls = {
            mockup: urls.mockup || null,
            logo: locationData.logo?.id ? (urls.logo || null) : null,
            text: locationData.personalization?.text ? (urls.text || null) : null
          };
        }

        customization.locations.push(locationData);
      });

      return { [key]: customization };
    },

    /**
     * Update cart attributes with customization data
     * Merges new customization with existing ones
     * @param {Object} newCustomization - New customization data to add
     * @returns {Promise<void>}
     */
    async updateCartCustomizations(newCustomization) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        let existingCustomizations = {};
        if (cart.attributes?._customizations) {
          try {
            existingCustomizations = typeof cart.attributes._customizations === 'string' ?
              JSON.parse(cart.attributes._customizations) : cart.attributes._customizations;
          } catch (e) {
            console.warn('[OrderAttributes] Failed to parse existing _customizations:', e);
          }
        }

        const mergedCustomizations = { ...existingCustomizations, ...newCustomization };

        const updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _customizations: JSON.stringify(mergedCustomizations)
            }
          })
        });

        if (!updateResponse.ok) {
          throw new Error('Failed to update cart attributes');
        }

      } catch (e) {
        console.error('[OrderAttributes] updateCartCustomizations failed:', e);
      }
    },

    /**
     * Generate minimal line item properties for cart
     * Only includes _customization_key - all display data comes from _customizations via JS
     * This keeps line item key stable (key = hash of properties, so fewer props = stable key)
     * @param {Object} customizationData - The customization data from buildCustomizationJSON
     * @returns {Object} Minimal properties object with only _customization_key
     */
    generateMinimalProperties(customizationData) {
      const props = {};

      const key = Object.keys(customizationData)[0];
      if (!key) return props;

      props['_customization_key'] = key;

      return props;
    },

    /**
     * Convenience method: Build and save customizations in one call
     * Saves for ALL sessions (not just B2B) - _customizations is now the source of truth
     * @param {Object} config - Same as buildCustomizationJSON config
     * @returns {Promise<{key: string, data: Object, minimalProperties: Object}>} The customization key, data, and minimal properties
     */
    async saveCustomizations(config) {
      let itemIndex = config.itemIndex;
      if (itemIndex === undefined || itemIndex === null) {
        itemIndex = await this.getNextItemIndex(config.variantId);
      }

      const customizationData = this.buildCustomizationJSON({
        ...config,
        itemIndex
      });

      await this.updateCartCustomizations(customizationData);

      const key = Object.keys(customizationData)[0];
      const minimalProperties = this.generateMinimalProperties(customizationData);

      return {
        key,
        data: customizationData,
        minimalProperties
      };
    },

    /**
     * Update an existing customization (for edit mode)
     * @param {string} customizationKey - The key to update (e.g., "variant_123_0")
     * @param {Object} config - Same as buildCustomizationJSON config (variantId will be extracted from key)
     * @returns {Promise<{key: string, data: Object, minimalProperties: Object}>}
     */
    async updateCustomization(customizationKey, config) {
      const parts = customizationKey.split('_');
      const variantId = parts[1];
      const itemIndex = parseInt(parts[2], 10);

      const customizationData = this.buildCustomizationJSON({
        ...config,
        variantId,
        itemIndex
      });

      await this.updateCartCustomizations(customizationData);

      const minimalProperties = this.generateMinimalProperties(customizationData);

      return {
        key: customizationKey,
        data: customizationData,
        minimalProperties
      };
    },

    /**
     * Remove a specific customization from cart attributes
     * @param {string} variantId - The variant ID
     * @param {number} itemIndex - The item index
     * @returns {Promise<void>}
     */
    async removeCustomization(variantId, itemIndex) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        if (!cart.attributes?._customizations) return;

        const customizations = typeof cart.attributes._customizations === 'string' ?
          JSON.parse(cart.attributes._customizations) : cart.attributes._customizations;

        const key = `variant_${variantId}_${itemIndex}`;
        if (customizations[key]) {
          delete customizations[key];

          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                _customizations: JSON.stringify(customizations)
              }
            })
          });

        }
      } catch (e) {}
    },

    /**
     * Clear all customizations from cart attributes
     * @returns {Promise<void>}
     */
    async clearAllCustomizations() {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              _customizations: ''
            }
          })
        });

        // Clear all IDB blobs so stale mockups are never served
        this._clearIdbBlobs();
      } catch (e) {}
    },

    /**
     * Delete IDB blobs for the given customization keys (all kinds + locations)
     */
    _deleteIdbBlobsForKeys(keys) {
      var svc = window.MultiLocationScreenshotService;
      if (!svc) return;
      var kinds = ['mockup', 'logo', 'text'];
      // We don't know which locationIds were used, so list IDB keys and match by prefix
      try {
        var dbName = 'customizer-mockups';
        var r = indexedDB.open(dbName, 1);
        r.onsuccess = function() {
          var db = r.result;
          var tx = db.transaction('blobs', 'readwrite');
          var store = tx.objectStore('blobs');
          var cursor = store.openCursor();
          cursor.onsuccess = function() {
            var c = cursor.result;
            if (!c) return;
            var idbKey = c.key;
            for (var i = 0; i < keys.length; i++) {
              for (var k = 0; k < kinds.length; k++) {
                if (typeof idbKey === 'string' && idbKey.startsWith(kinds[k] + ':' + keys[i] + ':')) {
                  c.delete();
                  break;
                }
              }
            }
            c.continue();
          };
        };
      } catch (e) {}
    },

    /**
     * Clear ALL IDB blobs (used when cart is emptied)
     */
    _clearIdbBlobs() {
      try {
        var r = indexedDB.open('customizer-mockups', 1);
        r.onsuccess = function() {
          var db = r.result;
          var tx = db.transaction('blobs', 'readwrite');
          tx.objectStore('blobs').clear();
        };
      } catch (e) {}
    },

    /**
     * Remove customizations for variants no longer in the cart
     * Call this on cart page load or after item removal
     * @returns {Promise<{removed: string[], kept: string[]}>}
     */
    async cleanupOrphanedCustomizations() {
      try {
        const cartResponse = await fetch('/cart.js?t=' + Date.now());
        const cart = await cartResponse.json();

        if (!cart.items || cart.items.length === 0) {
          if (cart.attributes?._customizations) {
            await this.clearAllCustomizations();
          }
          return { removed: [], kept: [] };
        }

        if (!cart.attributes?._customizations) {
          return { removed: [], kept: [] };
        }

        const customizations = typeof cart.attributes._customizations === 'string' ?
          JSON.parse(cart.attributes._customizations) : cart.attributes._customizations;

        const cartVariantIds = new Set(
          cart.items
            .filter(item => {
              const hp = window.helperProducts || {};
              const helperHandles = [hp.embroidery, hp.additionalLogos, hp.personalization].filter(Boolean);
              return !helperHandles.includes(item.handle);
            })
            .map(item => String(item.variant_id))
        );

        const removed = [];
        const kept = [];

        for (const key of Object.keys(customizations)) {
          const match = key.match(/^variant_(\d+)_\d+$/);
          if (match) {
            const variantId = match[1];
            if (!cartVariantIds.has(variantId)) {
              delete customizations[key];
              removed.push(key);
            } else {
              kept.push(key);
            }
          }
        }

        if (removed.length > 0) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                _customizations: JSON.stringify(customizations)
              }
            })
          });

          // Delete stale IDB blobs for removed keys
          this._deleteIdbBlobsForKeys(removed);
        }

        return { removed, kept };
      } catch (e) {
        return { removed: [], kept: [] };
      }
    }
  };
})();
